import { ChangeDetectionStrategy, Component, input, output, signal, ViewChild, ElementRef, HostListener } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';

export interface UploadFileConfig {
  title?: string;
  description?: string;
  allowedTypes?: string[]; // MIME types
  allowedExtensions?: string[]; // Extensiones con punto (ej: '.pdf', '.jpg')
  maxFileSize?: number; // En bytes
  multiple?: boolean; // Permitir múltiples archivos
  showComment?: boolean; // Mostrar campo de comentario
}

@Component({
  selector: 'app-upload-file',
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, MatChipsModule],
  templateUrl: './upload-file-dialog.component.html',
  styleUrl: './upload-file-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadFileComponent {
  // Inputs modernos con signals
  config = input<UploadFileConfig>({});

  // Outputs modernos - solo emite cambios, no acciones de guardar/cancelar
  filesChanged = output<File[]>(); // Emite cuando cambia la lista de archivos
  commentChanged = output<string>(); // Emite cuando cambia el comentario

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFiles = signal<File[]>([]);
  comment = signal<string>('');
  fileError = signal<string | null>(null);
  isDragging = signal<boolean>(false);

  // Configuración con valores por defecto
  private getConfig() {
    const cfg = this.config();
    return {
      title: cfg.title || 'Subir archivo(s)',
      description: cfg.description || '',
      allowedTypes: cfg.allowedTypes || [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
      ],
      allowedExtensions: cfg.allowedExtensions || ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'],
      maxFileSize: cfg.maxFileSize || 10 * 1024 * 1024, // 10MB por defecto
      multiple: cfg.multiple !== undefined ? cfg.multiple : true,
      showComment: cfg.showComment !== undefined ? cfg.showComment : true,
    };
  }

  get title() {
    return this.getConfig().title;
  }
  get description() {
    return this.getConfig().description;
  }
  get allowedTypes() {
    return this.getConfig().allowedTypes;
  }
  get allowedExtensions() {
    return this.getConfig().allowedExtensions;
  }
  get maxFileSize() {
    return this.getConfig().maxFileSize;
  }
  get multiple() {
    return this.getConfig().multiple;
  }
  get showComment() {
    return this.getConfig().showComment;
  }

  // Listener para Ctrl/Cmd + V
  @HostListener('window:paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      event.preventDefault();
      this.handleFiles(files);
    }
  }

  // Eventos de drag & drop
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
    }
  }

  private handleFiles(files: File[]): void {
    // Si no permite múltiples archivos, solo tomar el primero
    if (!this.multiple) {
      files = [files[0]];
    }

    const validFiles: File[] = [];
    let errorMessage: string | null = null;

    for (const file of files) {
      // Validar tipo de archivo
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isValidType = this.allowedTypes.includes(file.type) || this.allowedExtensions.includes(fileExtension);

      if (!isValidType) {
        errorMessage = `Tipo de archivo no permitido: ${file.name}. Solo se aceptan: ${this.allowedExtensions.join(', ')}`;
        break;
      }

      // Validar tamaño de archivo
      if (file.size > this.maxFileSize) {
        const maxSizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(2);
        errorMessage = `El archivo ${file.name} es demasiado grande. Tamaño máximo: ${maxSizeMB}MB`;
        break;
      }

      validFiles.push(file);
    }

    if (errorMessage) {
      this.fileError.set(errorMessage);
      this.resetFileInput();
      return;
    }

    // Si pasa las validaciones, establecer los archivos
    this.fileError.set(null);

    if (this.multiple) {
      // Agregar a los archivos existentes
      const currentFiles = this.selectedFiles();
      this.selectedFiles.set([...currentFiles, ...validFiles]);
    } else {
      // Reemplazar el archivo existente
      this.selectedFiles.set(validFiles);
    }

    // Emitir cambio
    this.filesChanged.emit(this.selectedFiles());
  }

  private resetFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  removeFile(index: number): void {
    const files = this.selectedFiles();
    files.splice(index, 1);
    this.selectedFiles.set([...files]);
    this.filesChanged.emit(this.selectedFiles()); // Emitir cambio

    if (files.length === 0) {
      this.resetFileInput();
    }

    this.fileError.set(null);
  }

  onCommentChange(value: string): void {
    this.comment.set(value);
    this.commentChanged.emit(value); // Emitir cambio
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getAcceptAttribute(): string {
    return this.allowedExtensions.join(',');
  }

  // Métodos públicos para que el padre pueda obtener los datos
  getFiles(): File[] {
    return this.selectedFiles();
  }

  getComment(): string {
    return this.comment();
  }

  hasFiles(): boolean {
    return this.selectedFiles().length > 0;
  }

  isValid(): boolean {
    return this.selectedFiles().length > 0;
  }

  // Método público para resetear el componente
  reset(): void {
    this.selectedFiles.set([]);
    this.comment.set('');
    this.fileError.set(null);
    this.resetFileInput();
  }
}
